import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { eventsApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import RichTextEditor from '../../components/ui/RichTextEditor'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import AdminImageField from '../../components/admin/AdminImageField'
import { buildImageFormData } from '../../utils/adminFormData'
import { formatDate } from '../../utils/date'

const EMPTY = {
  slug: '',
  title_en: '',
  title_ar: '',
  content_en: '',
  content_ar: '',
  location_en: '',
  location_ar: '',
  start_date: '',
  end_date: '',
  time: '',
  image: '',
  published: true,
  is_published: true,
}

function normalizeEvent(item) {
  const eventDate = item.event_date || item.start_date
  const endDate = item.event_end_date || item.end_date

  return {
    ...item,
    start_date: eventDate ? String(eventDate).slice(0, 10) : '',
    end_date: endDate ? String(endDate).slice(0, 10) : '',
    is_published: item.is_published ?? item.published ?? true,
    published: item.is_published ?? item.published ?? true,
  }
}

function buildEventPayload(form, imageFile) {
  const base = {
    slug: form.slug,
    title_en: form.title_en,
    title_ar: form.title_ar,
    content_en: form.content_en || form.title_en,
    content_ar: form.content_ar || form.title_ar,
    location_en: form.location_en || '',
    location_ar: form.location_ar || '',
    event_date: form.start_date || form.event_date,
    event_end_date: form.end_date || form.event_end_date || null,
    image: form.image || '',
    is_published: Boolean(form.is_published ?? form.published),
  }
  return buildImageFormData(base, form, imageFile)
}

export default function AdminEvents() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)

  const resetImageState = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
  }

  const handleImageSelect = (file) => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const load = () => {
    setLoading(true)
    eventsApi
      .getAllAdmin({ per_page: 100 })
      .then(({ data }) => setItems(data.data || data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = buildEventPayload(form, imageFile)
      if (form.id) await eventsApi.update(form.id, payload)
      else await eventsApi.create(payload)
      resetImageState()
      setForm(null)
      load()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.eventsAdmin.confirmDelete'))) return
    await eventsApi.delete(id)
    load()
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-primary">{t('admin.eventsAdmin.title')}</h1>
        <Button onClick={() => { resetImageState(); setForm(EMPTY) }} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.eventsAdmin.add')}
        </Button>
      </div>

      {form && (
        <Card className="mb-8">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.pages.slug')} htmlFor="event-slug" className="sm:col-span-2">
              <input id="event-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.pages.titleEn')} htmlFor="event-title-en">
              <input id="event-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.pages.titleAr')} htmlFor="event-title-ar">
              <input id="event-title-ar" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} />
            </AdminFormField>
            <AdminFormField label={t('admin.eventsAdmin.startDate')} htmlFor="event-start-date">
              <input id="event-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.eventsAdmin.endDate')} htmlFor="event-end-date">
              <input id="event-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('events.time')} htmlFor="event-time">
              <input id="event-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminImageField
              label={t('admin.newsAdmin.image')}
              htmlFor="event-image"
              className="sm:col-span-2"
              value={form.image}
              onChange={(image) => setForm({ ...form, image })}
              imageFile={imageFile}
              imagePreview={imagePreview}
              onFileSelect={handleImageSelect}
              disabled={saving}
            />
            <AdminFormField label={t('admin.pages.contentEn')} htmlFor="event-content-en" className="sm:col-span-2">
              <RichTextEditor
                value={form.content_en || ''}
                onChange={(html) => setForm({ ...form, content_en: html })}
                dir="ltr"
              />
            </AdminFormField>
            <AdminFormField label={t('admin.pages.contentAr')} htmlFor="event-content-ar" className="sm:col-span-2">
              <RichTextEditor
                value={form.content_ar || ''}
                onChange={(html) => setForm({ ...form, content_ar: html })}
                dir="rtl"
              />
            </AdminFormField>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input
                type="checkbox"
                checked={form.is_published ?? form.published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked, published: e.target.checked })}
              />
              {t('admin.pages.published')}
            </label>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>{t('buttons.save')}</Button>
              <Button type="button" variant="ghost" onClick={() => { resetImageState(); setForm(null) }}>{t('buttons.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-background">
              <tr>
                <th className="px-4 py-3 text-start">{t('admin.pages.titleEn')}</th>
                <th className="px-4 py-3 text-start">{t('admin.eventsAdmin.startDate')}</th>
                <th className="px-4 py-3 text-end">{t('admin.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-border">
                  <td className="px-4 py-3">{item.title_en}</td>
                  <td className="px-4 py-3">{formatDate(item.event_date || item.start_date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => { resetImageState(); setForm(normalizeEvent(item)) }} className="p-2 text-primary"><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={() => handleDelete(item.id)} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
